import React from "react";

const scoreColor = (v) => {
  if (v >= 80) return "#10B981";
  if (v >= 65) return "#84CC16";
  if (v >= 50) return "#F59E0B";
  if (v >= 35) return "#FB923C";
  return "#F43F5E";
};

const riskColor = (level) => ({
  Low: "#10B981", Moderate: "#F59E0B", Elevated: "#FB923C", High: "#F43F5E",
}[level] || "#F59E0B");

const ScoreRing = ({ value, color }) => {
  const r = 42, circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value)) / 100;
  return (
    <svg viewBox="0 0 100 100" className="h-[92px] w-[92px] -rotate-90">
      <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
      <circle
        cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
        style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)" }}
      />
    </svg>
  );
};

const ScoreBar = ({ label, value, hint }) => (
  <div>
    <div className="mb-1 flex items-baseline justify-between">
      <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">{label}</span>
      <span className="font-mono text-sm font-semibold tabular-nums" style={{ color: scoreColor(value) }}>{value}<span className="text-[10px] text-zinc-600"> / 100</span></span>
    </div>
    <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800/70">
      <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${value}%`, background: scoreColor(value) }} />
    </div>
    {hint && <p className="mt-1 text-[11px] leading-snug text-zinc-500">{hint}</p>}
  </div>
);

// LONGVIEW at a glance — proprietary intelligence score.
export const ScorePanel = ({ scores }) => {
  if (!scores || scores.business_quality == null) return null;
  const primaryColor = scoreColor(scores.business_quality);
  return (
    <div data-testid="company-longview-at-a-glance" className="lv-panel lv-top-edge p-5">
      <div className="mb-1 flex items-center justify-between">
        <div className="kicker">LONGVIEW Score</div>
        <span className="rounded border border-indigo-500/25 bg-indigo-500/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-indigo-300">Proprietary Rating</span>
      </div>
      <p className="mb-4 text-[11px] leading-relaxed text-zinc-500">A simple 0–100 read on the business. Illustrative only — not a recommendation or a price target.</p>

      <div className="mb-4 flex items-center gap-4 border-b border-white/[0.06] pb-4">
        <div className="relative flex h-[92px] w-[92px] shrink-0 items-center justify-center">
          <ScoreRing value={scores.business_quality} color={primaryColor} />
          <div className="absolute flex flex-col items-center">
            <span className="font-mono text-2xl font-bold tabular-nums text-white">{scores.business_quality}</span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">/ 100</span>
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Business Quality</div>
          <p className="mt-1 text-xs leading-relaxed text-zinc-400">LONGVIEW's composite read on the underlying business — not price, purely fundamentals.</p>
        </div>
      </div>

      <div className="space-y-3.5">
        <ScoreBar label="Financial Health" value={scores.financial_health} />
        <ScoreBar label="Growth Potential" value={scores.growth} />
        <ScoreBar label="Valuation" value={scores.valuation} hint={`Reads as: ${scores.valuation_label}`} />
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-white/[0.06] pt-3">
        <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">Risk Level</span>
        <span className="flex items-center gap-1.5 font-mono text-sm font-semibold" style={{ color: riskColor(scores.risk_level) }}>
          <span className="h-2 w-2 rounded-full" style={{ background: riskColor(scores.risk_level) }} />
          {scores.risk_level}
        </span>
      </div>
    </div>
  );
};
